import { useMutation, useQuery, useQueryClient } from "react-query";
import CustomerApiService from "../../CustomerApiService";
import { CollectionPreview, ImportProfile } from "./mockData";

/**
 * `metadata-import` data layer.
 *
 * Profiles are persisted by iconik-manager and fetched via apps/customer-api, then adapted into
 * the `ImportProfile` display shape the panels render.
 *
 * PERFORMANCE: opening a profile fetches only the persisted config (fast, DynamoDB-backed) — it does
 * NOT block on the iconik-backed reconciliation. Overview loads the preview asynchronously via
 * `usePreview`. Caching is configured PER QUERY here (not on the shared Customer Portal QueryClient):
 * stable Metadata Import data gets a `staleTime` and no refetch-on-window-focus, so tab navigation
 * and refocus don't re-hit iconik while the data is still fresh. Mutations invalidate the exact keys.
 */

/* --- metadata-import query caching (local to this feature, never global) --- */
const MI_STALE = 45_000; // 45s: config/columns/runs are stable within a session of editing
const CATALOG_STALE = 5 * 60_000; // field catalog rarely changes; cache 5 min
const miOpts = { staleTime: MI_STALE, refetchOnWindowFocus: false } as const;

/* ------------------------------------------------------------------ wire types (mirror manager) */
export interface WireProfile {
  id: string;
  systemDomainId: string;
  name: string;
  workflow: "collection-sync" | "asset-import";
  status: "draft" | "active" | "paused";
  sourceShape: string;
  source: {
    format: string;
    assetId: string;
    valueDelimiter?: string;
    adapterConfig?: Record<string, unknown>;
  };
  mappings: {
    sourceColumn: string;
    targetFieldName: string;
    strategy: "append" | "overwrite";
    /** Per-mapping multi-value separator (multi-value target fields only); falls back to source. */
    valueDelimiter?: string;
  }[];
  targeting: {
    mode: string;
    /** Legacy single root — still accepted; superseded by `collectionIds`. */
    collectionId?: string;
    /** One or more collection roots this workflow applies to. */
    collectionIds?: string[];
    includeDescendants?: boolean;
  };
  writeDestination: string;
  schedule: {
    mode: "manual" | "daily" | "weekly";
    at?: string;
    weekday?: string;
    timezone?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface FieldDescriptor {
  name: string;
  label: string;
  fieldType: string;
  multi: boolean;
  readOnly: boolean;
  allowedValues: string[];
}
export interface PickerItem {
  id: string;
  title: string;
}
export interface SourceColumns {
  format: string;
  columns: { name: string; sample: string }[];
  recordCount: number;
  warnings: { location?: string; message: string }[];
  /** Format-specific detection hints (xlsx: sheetNames/suggestedHeaderRow; json/xml: record paths). */
  detection?: {
    kind?: string;
    sheetNames?: string[];
    suggestedWorksheetIndex?: number;
    suggestedHeaderRow?: number;
    topLevelShape?: string;
    root?: string;
    suggestedRecordPath?: string;
    candidateRecordPaths?: string[];
    error?: string;
  };
}
/** A Metadata Import run == a canonical Manager Job (there is no separate run store). The bounded
 *  MI counts live in the Job's `metadata`; status is the canonical Manager job status. */
export interface RunJobMetadata {
  feature: "metadata-import";
  workflowId: string;
  workflowName?: string;
  trigger: "manual" | "scheduled";
  /** Precise MI outcome the UI distinguishes: executed|empty|planned|partial|failed|stale|refused-*. */
  outcome: string;
  writesEnabled: boolean;
  revision?: string;
  planRevision?: string;
  targeted?: number;
  changed?: number;
  unchanged?: number;
  failed?: number;
  warnings?: number;
  failureSummary?: { assetId: string; assetTitle: string; error: string }[];
}

export interface RunSummary {
  jobId: string;
  /** Canonical Manager job status: success | warning | error | running | retrying | waiting | aborted. */
  status: string;
  startedAt?: string;
  secondsElapsed?: number;
  metadata?: RunJobMetadata;
}

export type RunTone = "ok" | "warn" | "danger" | "running" | "info";

/**
 * Customer-facing outcome interpretation. Manager's native Job status is coarse (success/warning/
 * error); Metadata Import refines it from `metadata.outcome` so the operator reads the TRUTH:
 *  - stale → "Stale — not executed" (a success Job that deliberately wrote nothing; must NOT look
 *    like an ordinary successful run).
 *  - planned → "Planned — writes disabled" (reconciled, but no metadata was written — the local
 *    writes-OFF state; must NOT imply a completed write).
 *  - a real zero-difference success (empty, or executed with 0 changes) → "Completed — no changes
 *    needed" (kept DISTINCT from planned/writes-disabled).
 * Falls back to the coarse status when there's no precise outcome yet (e.g. still running).
 */
export function runDisplay(input: {
  status: string;
  outcome?: string;
  changed?: number;
}): { label: string; tone: RunTone } {
  const { status, outcome, changed } = input;
  if (outcome) {
    if (outcome === "stale") {
      return { label: "Stale — not executed", tone: "warn" };
    }
    if (outcome === "planned") {
      return { label: "Planned — writes disabled", tone: "info" };
    }
    if (outcome.startsWith("refused")) {
      return { label: "Refused — not run", tone: "danger" };
    }
    if (outcome === "empty") {
      return { label: "Completed — no changes needed", tone: "ok" };
    }
    if (outcome === "executed") {
      return changed === 0
        ? { label: "Completed — no changes needed", tone: "ok" }
        : { label: "Completed", tone: "ok" };
    }
    if (outcome === "partial") {
      return { label: "Completed · warnings", tone: "warn" };
    }
    if (outcome === "failed") {
      return { label: "Failed", tone: "danger" };
    }
  }
  // Fallback on the coarse status (running states, unknown outcomes).
  if (status === "warning") {
    return { label: "Completed · warnings", tone: "warn" };
  }
  if (status === "error" || status === "failed" || status === "aborted") {
    return { label: "Failed", tone: "danger" };
  }
  if (status === "running" || status === "retrying" || status === "waiting") {
    return { label: "Running", tone: "running" };
  }
  return { label: "Completed", tone: "ok" };
}

export interface ExecutionPlan {
  revision: string;
  planRevision: string;
  targeted: number;
  assetsToWrite: number;
  fieldChanges: number;
  unchanged: number;
  blocked: { assetId: string; assetTitle: string; errors: string[] }[];
  strategies: { fieldName: string; strategy: "append" | "overwrite" }[];
  items: {
    assetId: string;
    assetTitle: string;
    fields: {
      fieldName: string;
      strategy: "append" | "overwrite";
      currentValues: string[];
      nextValues: string[];
    }[];
  }[];
  warnings: { location?: string; message: string }[];
}
export interface ExecutionOutcome {
  status: "planned" | "executed" | "partial" | "failed" | "stale" | "empty";
  committed: boolean;
  plan: ExecutionPlan;
  written?: number;
  failed?: number;
  reviewedRevision?: string;
  reviewedPlanRevision?: string;
}

/* ------------------------------------------------------------------ query keys */
export const miQueryKeys = {
  profiles: ["metadata-import", "profiles"] as const,
  config: (id: string) => ["metadata-import", "config", id] as const,
  preview: (id: string) => ["metadata-import", "preview", id] as const,
  runs: (id: string) => ["metadata-import", "runs", id] as const,
  fieldCatalog: ["metadata-import", "fieldCatalog"] as const,
  sourceColumns: (assetId: string) =>
    ["metadata-import", "sourceColumns", assetId] as const,
  assetTitle: (id: string) => ["metadata-import", "assetTitle", id] as const,
  collectionTitle: (id: string) =>
    ["metadata-import", "collectionTitle", id] as const,
};

const api = CustomerApiService;

/** Effective, de-duplicated Collection Sync roots — normalizes the legacy single `collectionId`
 *  and the multi `collectionIds` into one canonical list (mirrors the backend `collectionSyncRoots`). */
export function collectionRoots(targeting: WireProfile["targeting"]): string[] {
  const raw = targeting.collectionIds?.length
    ? targeting.collectionIds
    : targeting.collectionId
    ? [targeting.collectionId]
    : [];
  return Array.from(new Set(raw.filter(Boolean)));
}

/* ------------------------------------------------------------------ adapter: WireProfile -> ImportProfile (display) */
export function wireToDisplay(w: WireProfile): ImportProfile {
  return {
    id: w.id,
    name: w.name,
    workflow: w.workflow,
    format: (w.source.format as ImportProfile["format"]) ?? "csv",
    status: w.status,
    description:
      w.workflow === "collection-sync"
        ? "Live sandbox-backed Collection Sync workflow — configuration is persisted; preview/execute run against real iconik."
        : "Asset Import workflow (configuration only — execution lands in a later slice).",
    schedule:
      w.schedule.mode === "manual"
        ? { mode: "manual" }
        : {
            mode: "scheduled",
            cadence: w.schedule.mode === "weekly" ? "weekly" : "daily",
            weekday: w.schedule.weekday,
            at: w.schedule.at,
            // No next-run is fabricated — unattended scheduling is not active.
          },
    source: {
      format: (w.source.format as ImportProfile["format"]) ?? "csv",
      // Human-readable title is resolved asynchronously on Overview (useAssetTitle); id is the fallback.
      assetTitle: w.source.assetId || "— none selected",
      assetId: w.source.assetId,
      lastModified: "",
      parseStatus: w.source.assetId ? "ok" : "pending",
      columnCount: 0,
      rowCount: 0,
      columns: [],
    },
    fields: w.mappings.map((m) => ({
      sourceColumn: m.sourceColumn,
      iconikField: m.targetFieldName,
      iconikFieldType: "",
      strategy: m.strategy,
    })),
    unmappedColumns: [],
    collectionTarget:
      w.targeting.mode === "collection-sync"
        ? {
            collectionPath:
              collectionRoots(w.targeting)[0] || "— none selected",
            collectionId: collectionRoots(w.targeting)[0] ?? "",
            collectionIds: collectionRoots(w.targeting),
            includeDescendants: w.targeting.includeDescendants ?? true,
            assetCount: 0,
          }
        : undefined,
    preview: {
      kind: "collection-sync",
      targeted: 0,
      wouldChange: 0,
      unchanged: 0,
      errors: 0,
      rows: [],
    },
  };
}

/* ------------------------------------------------------------------ profiles list
 * DO NOT swallow failures — a backend/auth error must surface as react-query `isError` (with
 * `refetch`), never be flattened into an empty list that looks like "no saved profiles." */
export async function fetchRealProfiles(): Promise<ImportProfile[]> {
  const real = await api.fetch<WireProfile[]>(
    "GET",
    "/metadata-import/profiles"
  );
  return real.map((w) => wireToDisplay(w));
}
export function useImportProfiles() {
  return useQuery(miQueryKeys.profiles, fetchRealProfiles, miOpts);
}

/** Raw persisted profile (the editors work on the real shape). */
export function useProfileConfig(id: string | undefined) {
  return useQuery(
    miQueryKeys.config(id ?? ""),
    () => api.fetch<WireProfile>("GET", `/metadata-import/profiles/${id}`),
    { enabled: !!id, ...miOpts }
  );
}

/**
 * The display shell for one workflow. Derives from the SAME config query the editors / SaveProvider
 * use (`useProfileConfig`) — so the workflow page fetches `/profiles/:id` ONCE, not twice under two
 * query keys.
 */
export function useImportProfile(id: string | undefined) {
  const cfg = useProfileConfig(id);
  return {
    ...cfg,
    data: cfg.data ? wireToDisplay(cfg.data) : undefined,
  };
}

/** Async reconciliation preview — never blocks the profile shell; loads on Overview. Read-only. */
export function usePreview(id: string | undefined, enabled: boolean) {
  return useQuery(
    miQueryKeys.preview(id ?? ""),
    () =>
      api.fetch<CollectionPreview>(
        "GET",
        `/metadata-import/profiles/${id}/preview`
      ),
    {
      enabled: !!id && enabled,
      staleTime: MI_STALE,
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );
}

/* ------------------------------------------------------------------ mutations */
export function useCreateProfile() {
  const qc = useQueryClient();
  return useMutation(
    (body: { name: string; workflow: string }) =>
      api.fetch<WireProfile>("POST", "/metadata-import/profiles", body),
    { onSuccess: () => qc.invalidateQueries(miQueryKeys.profiles) }
  );
}
/** Hard-delete a persisted profile. The profile row is removed; its historical run records live in
 *  a separate store and are NOT deleted by this. Drops the profile's cached queries and refreshes
 *  the list so the deleted profile disappears and its stale detail route can't resolve. */
export function useDeleteProfile() {
  const qc = useQueryClient();
  return useMutation(
    (id: string) =>
      api.fetch<{ ok: boolean }>("DELETE", `/metadata-import/profiles/${id}`),
    {
      onSuccess: (_data, id) => {
        qc.removeQueries(miQueryKeys.config(id));
        qc.removeQueries(miQueryKeys.preview(id));
        qc.removeQueries(miQueryKeys.runs(id));
        qc.invalidateQueries(miQueryKeys.profiles);
      },
    }
  );
}

export function useUpdateProfile(id: string) {
  const qc = useQueryClient();
  return useMutation(
    (patch: Partial<WireProfile>) =>
      api.fetch<WireProfile>("PUT", `/metadata-import/profiles/${id}`, patch),
    {
      onSuccess: () => {
        qc.invalidateQueries(miQueryKeys.config(id));
        qc.invalidateQueries(miQueryKeys.preview(id)); // config changed → reconciliation is stale
        qc.invalidateQueries(miQueryKeys.profiles);
      },
    }
  );
}

/* ------------------------------------------------------------------ field catalog (loaded once, cached, filtered locally) */
export function useFieldCatalog() {
  return useQuery(
    miQueryKeys.fieldCatalog,
    () =>
      api.fetch<FieldDescriptor[]>(
        "GET",
        "/metadata-import/metadata-fields?q="
      ),
    { staleTime: CATALOG_STALE, refetchOnWindowFocus: false }
  );
}

/* ------------------------------------------------------------------ pickers + runs + title resolve */
export function useSearchAssets(q: string) {
  return useQuery(
    ["metadata-import", "assets", q],
    () =>
      api.fetch<PickerItem[]>(
        "GET",
        `/metadata-import/assets/search?q=${encodeURIComponent(q)}`
      ),
    // Empty query fetches RECENT candidates (shown before the operator types); a 1-char query is
    // ignored; 2+ chars searches. All cached separately by the query key.
    { enabled: q.length === 0 || q.length >= 2, refetchOnWindowFocus: false }
  );
}
export function useSearchCollections(q: string) {
  return useQuery(
    ["metadata-import", "collections", q],
    () =>
      api.fetch<PickerItem[]>(
        "GET",
        `/metadata-import/collections/search?q=${encodeURIComponent(q)}`
      ),
    { enabled: q.length >= 1, refetchOnWindowFocus: false }
  );
}
export function useSourceColumns(
  assetId: string | undefined,
  format?: string,
  config?: Record<string, unknown>
) {
  const fmt = format ?? "csv";
  const cfgStr = JSON.stringify(config ?? {});
  return useQuery(
    [...miQueryKeys.sourceColumns(assetId ?? ""), fmt, cfgStr],
    () =>
      api.fetch<SourceColumns>(
        "GET",
        `/metadata-import/source/columns?assetId=${encodeURIComponent(
          assetId ?? ""
        )}&format=${encodeURIComponent(fmt)}&config=${encodeURIComponent(
          cfgStr
        )}`
      ),
    { enabled: !!assetId, ...miOpts }
  );
}
export function useRuns(id: string | undefined) {
  return useQuery(
    miQueryKeys.runs(id ?? ""),
    () =>
      api.fetch<RunSummary[]>("GET", `/metadata-import/profiles/${id}/runs`),
    { enabled: !!id, ...miOpts }
  );
}
export function useAssetTitle(id: string | undefined) {
  return useQuery(
    miQueryKeys.assetTitle(id ?? ""),
    () =>
      api.fetch<PickerItem>(
        "GET",
        `/metadata-import/resolve/asset/${encodeURIComponent(id ?? "")}`
      ),
    {
      enabled: !!id,
      staleTime: CATALOG_STALE,
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );
}
/** The collections a source asset belongs to — Applies To suggests these first. Human-readable. */
export function useSourceCollections(assetId: string | undefined) {
  return useQuery(
    ["metadata-import", "assetCollections", assetId ?? ""],
    () =>
      api.fetch<PickerItem[]>(
        "GET",
        `/metadata-import/resolve/asset/${encodeURIComponent(
          assetId ?? ""
        )}/collections`
      ),
    {
      enabled: !!assetId,
      staleTime: CATALOG_STALE,
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );
}

export function useCollectionTitle(id: string | undefined) {
  return useQuery(
    miQueryKeys.collectionTitle(id ?? ""),
    () =>
      api.fetch<PickerItem>(
        "GET",
        `/metadata-import/resolve/collection/${encodeURIComponent(id ?? "")}`
      ),
    {
      enabled: !!id,
      staleTime: CATALOG_STALE,
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );
}

/** commit:true returns the started Manager Job id (poll Run history); commit:false returns the
 *  synchronous review plan (ExecutionOutcome). */
export interface JobStart {
  jobId: string;
}

export function useExecuteImportProfile(profileId: string | undefined) {
  return useMutation(
    (body: {
      reviewedRevision?: string;
      reviewedPlanRevision?: string;
      commit: boolean;
    }) =>
      api.fetch<ExecutionOutcome | JobStart>(
        "POST",
        `/metadata-import/profiles/${profileId ?? ""}/execute`,
        body
      )
  );
}
